import type { Assignment } from '@/types/domain';
import { DevSwitch } from '@/types/domain';
import { getDb } from '@/data/db';
import { StorageKeys } from '@/lib/config';

/** The driver number the lists are scoped to (native filters by rem_f01_user). */
function currentDriver(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(StorageKeys.driverNumber);
}

/** True when the assignment belongs to the logged-in driver (or no driver set). */
function ownedByDriver(a: Assignment): boolean {
  const driver = currentDriver();
  if (!driver) return true;
  return (a.remF01User ?? '') === driver;
}

/**
 * Helpers for reading and mutating the local assignment cache.
 * UI components should subscribe via dexie-react-hooks `useLiveQuery` to
 * remain reactive without manual refetch.
 */
export const assignmentRepo = {
  async getAll(): Promise<Assignment[]> {
    return getDb().assignments.orderBy('remModifiedTimestamp').reverse().toArray();
  },

  async getById(id: string): Promise<Assignment | undefined> {
    return getDb().assignments.get(id);
  },

  /**
   * Items eligible for the Load screen: local switch === 1 (new), scoped to the
   * logged-in driver. Matches the native `getNewAssignments(driver)` filter; a
   * bill only leaves the load list once pickup completes (switch -> 2).
   */
  async getLoadItems(): Promise<Assignment[]> {
    return getDb()
      .assignments.where('devF31SwitchUpd')
      .equals(DevSwitch.New)
      .filter(ownedByDriver)
      .toArray();
  },

  /**
   * Items eligible for the Unload screen: local switch 2 (picked up, ready
   * to deliver) AND 5 (delivery/unloading in progress), scoped to the
   * logged-in driver. Switch 5 must stay visible: an app reload mid-delivery
   * (update banner, browser eviction) wipes the in-memory flow state, and a
   * 5-only-invisible bill made the whole call vanish from every list until
   * dispatch re-dispatched it. Re-entering the stop restarts the delivery
   * flow, which is the recovery path.
   */
  async getUnloadItems(): Promise<Assignment[]> {
    return getDb()
      .assignments.where('devF31SwitchUpd')
      .anyOf(DevSwitch.Pickup, DevSwitch.Delivery)
      .filter(ownedByDriver)
      .toArray();
  },

  /**
   * Active assignments for route-level GPS tracking: switch 2 (picked up, en
   * route to deliver) or 5 (delivery in progress), scoped to the driver.
   * Mirrors native `getPickupAssignments` + `getDeliveryAssignments`.
   */
  async getActiveAssignments(): Promise<Assignment[]> {
    return getDb()
      .assignments.where('devF31SwitchUpd')
      .anyOf(DevSwitch.Pickup, DevSwitch.Delivery)
      .filter(ownedByDriver)
      .toArray();
  },

  async setLocalSwitch(id: string, value: number): Promise<void> {
    await getDb().assignments.update(id, { devF31SwitchUpd: value });
  },

  /** Update dispatch routing locally (native delivery dead-call/no-freight reset to '2'). */
  async updateDispatchType(id: string, newType: string): Promise<void> {
    await getDb().assignments.update(id, { remDispatchType: newType });
  },

  async markComplete(id: string): Promise<void> {
    await getDb().assignments.update(id, { devF31SwitchUpd: DevSwitch.Complete });
  },

  /**
   * Delete assignments reassigned away from this driver (server flipped
   * rem_f31_switch to 75). Applies regardless of local progress -- the server
   * decision wins, matching the re-dispatch rules. Returns how many local
   * records were actually removed (unknown ids are ignored).
   */
  async removeAssignments(ids: string[]): Promise<number> {
    if (ids.length === 0) return 0;
    return getDb().assignments.where('remF04Id').anyOf(ids).delete();
  },

  /**
   * Reconcile the local cache after a FULL load (a sync with no lastSync:
   * fresh install, cleared storage, or sign-out/sign-in). The server response
   * is the complete set of this driver's open calls, so any local assignment
   * NOT in it is stale: archived by TMS (switch 99999), reassigned before this
   * device saw the 75, or left over from an earlier flood. Deleting matches
   * the switch-75 behavior; queued logs and media are independent rows and
   * still upload. Returns how many local records were removed.
   */
  async pruneToOpenSet(openIds: string[]): Promise<number> {
    const keep = new Set(openIds);
    return getDb()
      .assignments.filter((a) => !keep.has(a.remF04Id))
      .delete();
  },
};
